import { useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import PropTypes from "prop-types";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormHelperText, Tooltip } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import { formatNumber } from "@/features/shared";

export const ProductVariantsSection = ({ editMode = false }) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "salesInvoiceProductVariants",
  });

  const productVariants = useWatch({
    control,
    name: "salesInvoiceProductVariants",
  });

  useEffect(() => {
    const subtotal = productVariants.reduce((sum, variant) => {
      return sum + (variant.quantity * variant.price - variant.discount);
    }, 0);

    setValue("subtotal", subtotal);
  }, [productVariants, setValue]);

  const handleDeleteClick = useCallback(
    (id) => () => {
      // Find index of the row with the matching id
      const index = productVariants.findIndex((variant) => variant.id === id);

      if (index !== -1) {
        remove(index);
        showSnackbar("Produk berhasil dihapus", "success");
      }
    },
    [productVariants, remove, showSnackbar],
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Nama",
        flex: 5,
        renderCell: ({ row: { name, sku } }) => (
          <ProductVariantNameCell name={name} sku={sku} />
        ),
      },
      { field: "quantity", headerName: "Qty", type: "number", width: 80 },
      { field: "unitOfMeasurement", headerName: "Unit", width: 60 },
      ...(editMode
        ? [
            {
              field: "returnedQuantity",
              headerName: "Retur",
              type: "number",
              valueFormatter: (value) => formatNumber(value),
              flex: 1,
            },
          ]
        : []),
      {
        field: "price",
        headerName: "@Harga",
        type: "number",
        flex: 1.5,
        editable: true,
        valueFormatter: (_, row) => formatNumber(row.price || 0),
      },
      {
        field: "discount",
        headerName: "Diskon",
        type: "number",
        editable: true,
        valueSetter: (value, row) => {
          const newValue = value == null ? 0 : value; // Convert null or undefined to 0
          return { ...row, discount: newValue };
        },
        valueFormatter: (_, row) => formatNumber(row.discount || 0),
      },
      {
        field: "totalPrice",
        headerName: "Total Harga",
        type: "number",
        width: 130,
        valueGetter: (_, row) => {
          const { quantity, price, discount } = row;

          return formatNumber(quantity * price - discount || 0);
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Aksi",
        getActions: ({ id, row }) => {
          // In edit mode, check if item has returns
          const hasReturns = editMode && row.returnedQuantity > 0;

          if (hasReturns) {
            return [
              <Tooltip
                key={id}
                title="Tidak dapat dihapus karena sudah ada retur"
              >
                <span>
                  <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Hapus"
                    disabled
                  />
                </span>
              </Tooltip>,
            ];
          }

          return [
            <GridActionsCellItem
              key={id}
              icon={<DeleteIcon />}
              label="Hapus"
              className="textPrimary"
              onClick={handleDeleteClick(id)}
              color="error"
            />,
          ];
        },
      },
    ],
    [editMode, handleDeleteClick],
  );

  // In edit mode, disable editing for rows with returns
  const isCellEditable = (params) => {
    if (!editMode) return true;

    // Check if the row has returns
    const hasReturns = params.row.returnedQuantity > 0;
    if (hasReturns) {
      return false;
    }

    return true;
  };

  const processRowUpdate = async (newRow) => {
    return new Promise((resolve, reject) => {
      // Clearing a numeric cell commits null (MUI's numeric valueParser), which
      // slips past the guards below and reaches the payload. Coerce to 0 first.
      const row = {
        ...newRow,
        quantity: Number(newRow.quantity) || 0,
        price: Number(newRow.price) || 0,
        discount: Number(newRow.discount) || 0,
      };
      const { quantity, price, discount } = row;
      const totalCost = quantity * price;

      // Validation logic
      if (quantity < 1) {
        reject(new Error("Kuantitas tidak boleh kurang dari 1"));
        return;
      }

      if (price < 0) {
        reject(new Error("Harga tidak boleh kurang dari 0"));
        return;
      }

      if (discount < 0) {
        reject(new Error("Diskon tidak boleh kurang dari 0"));
        return;
      }

      if (discount > totalCost) {
        reject(new Error("Diskon tidak boleh lebih besar dari Total Harga"));
        return;
      }

      // Find the index and update the form state
      const index = productVariants.findIndex(
        (variant) => variant.id === row.id,
      );

      if (index !== -1) {
        update(index, row);
        resolve(row);
      } else {
        reject(new Error("Produk tidak ditemukan. Muat ulang halaman lalu coba lagi."));
      }
    });
  };

  return (
    <>
      {errors.salesInvoiceProductVariants && (
        <FormHelperText error>
          ERROR: {errors.salesInvoiceProductVariants.message}
        </FormHelperText>
      )}
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={productVariants}
        isCellEditable={isCellEditable}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          showSnackbar(error.message, "error");
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
    </>
  );
};

ProductVariantsSection.propTypes = {
  editMode: PropTypes.bool,
};
